import React, { useState, useEffect } from 'react';
import { getAdminUsers, getAdminFeedback, getAdminWaitlist, sendAdminPasswordReset, getAdminUserClients, getAdminClients, adminDeleteClient, getAdminUsage } from '../api/client';
import LoadingLeaf from '../components/LoadingLeaf';

export default function Admin() {
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [usage, setUsage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [resettingUserId, setResettingUserId] = useState(null);
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [userClients, setUserClients] = useState({});
  const [loadingClientsFor, setLoadingClientsFor] = useState(null);
  const [deletingClientId, setDeletingClientId] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 4000);
  };

  const handleSendReset = async (u) => {
    if (!window.confirm(`Send a password reset email to ${u.email}?`)) return;
    setResettingUserId(u.user_id);
    try {
      const res = await sendAdminPasswordReset(u.user_id);
      showToast(res.data?.message || 'Password reset email sent.');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not send password reset email.');
    } finally {
      setResettingUserId(null);
    }
  };

  const toggleExpandUser = async (u) => {
    if (expandedUserId === u.user_id) {
      setExpandedUserId(null);
      return;
    }
    setExpandedUserId(u.user_id);
    if (!userClients[u.user_id]) {
      setLoadingClientsFor(u.user_id);
      try {
        const res = await getAdminUserClients(u.user_id);
        setUserClients(prev => ({ ...prev, [u.user_id]: res.data || [] }));
      } catch (err) {
        showToast('Could not load clients for this user.');
      } finally {
        setLoadingClientsFor(null);
      }
    }
  };

  const handleDeleteClient = async (userId, client) => {
    if (!window.confirm(`Delete "${client.name}" and all their sessions, messages, and goals? This cannot be undone.`)) return;
    setDeletingClientId(client.id);
    try {
      await adminDeleteClient(client.id);
      setUserClients(prev => prev[userId] ? { ...prev, [userId]: prev[userId].filter(c => c.id !== client.id) } : prev);
      setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, client_count: Math.max(0, u.client_count - 1) } : u));
      setClients(prev => prev.filter(c => c.id !== client.id));
      showToast(`${client.name} deleted.`);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not delete client.');
    } finally {
      setDeletingClientId(null);
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [usersRes, feedbackRes, waitlistRes, clientsRes, usageRes] = await Promise.all([
        getAdminUsers(), getAdminFeedback(), getAdminWaitlist(), getAdminClients(), getAdminUsage(),
      ]);
      setUsers(usersRes.data || []);
      setFeedback(feedbackRes.data || []);
      setWaitlist(waitlistRes.data || []);
      setClients(clientsRes.data || []);
      setUsage(usageRes.data || []);
    } catch (err) {
      setError(err.response?.status === 403
        ? "You don't have access to this page."
        : 'Could not load admin data.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('default', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const totalUsageCost = usage.reduce((sum, u) => sum + (u.estimated_cost_usd || 0), 0);

  const tabs = [
    { id: 'users', label: `Users (${users.length})` },
    { id: 'clients', label: `Clients (${clients.length})` },
    { id: 'feedback', label: `Feedback (${feedback.length})` },
    { id: 'waitlist', label: `Waitlist (${waitlist.length})` },
    { id: 'usage', label: `Usage ($${totalUsageCost.toFixed(0)})` },
  ];

  const filteredClients = clients.filter(c => {
    const q = clientSearch.toLowerCase();
    return !q || c.name?.toLowerCase().includes(q) || c.coach_email?.toLowerCase().includes(q) || c.coach_name?.toLowerCase().includes(q);
  });

  if (loading) return <div style={styles.loadingState}><LoadingLeaf size={56} /></div>;
  if (error) return <div style={styles.page}><div style={styles.errorAlert}>{error}</div></div>;

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div style={styles.pageTitle}>Admin</div>
        <div style={styles.pageSubtitle}>Users, clients, feedback, waitlist signups, and AI usage across the whole app</div>
      </div>

      {toast && <div style={styles.toast}>{toast}</div>}

      <div style={styles.tabRow}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            style={{ ...styles.tab, ...(activeTab === tab.id ? styles.tabActive : {}) }}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'users' && (
        <div style={styles.card}>
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Name / Practice</th>
                  <th style={styles.th}>Onboarded</th>
                  <th style={styles.th}>Clients</th>
                  <th style={styles.th}>Sessions</th>
                  <th style={styles.th}>Signed up</th>
                  <th style={styles.th}>Last active</th>
                  <th style={styles.th}></th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <React.Fragment key={u.user_id}>
                    <tr>
                      <td style={styles.td}>{u.email}</td>
                      <td style={styles.td}>
                        <div>{u.full_name || '—'}</div>
                        {u.practice_name && <div style={styles.tdSub}>{u.practice_name}</div>}
                      </td>
                      <td style={styles.td}>
                        <span style={{ ...styles.pill, ...(u.onboarding_completed ? styles.pillGood : styles.pillMuted) }}>
                          {u.onboarding_completed ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        {u.client_count > 0 ? (
                          <button style={styles.linkBtn} onClick={() => toggleExpandUser(u)}>
                            {expandedUserId === u.user_id ? '▲' : '▼'} {u.client_count}
                          </button>
                        ) : u.client_count}
                      </td>
                      <td style={styles.td}>{u.session_count}</td>
                      <td style={styles.td}>{formatDate(u.signed_up_at)}</td>
                      <td style={styles.td}>{formatDate(u.last_sign_in_at)}</td>
                      <td style={styles.td}>
                        <button
                          style={styles.actionBtn}
                          onClick={() => handleSendReset(u)}
                          disabled={resettingUserId === u.user_id}
                        >
                          {resettingUserId === u.user_id ? 'Sending...' : 'Send password reset'}
                        </button>
                      </td>
                    </tr>
                    {expandedUserId === u.user_id && (
                      <tr>
                        <td style={styles.expandedTd} colSpan={8}>
                          {loadingClientsFor === u.user_id ? (
                            <div style={styles.tdSub}>Loading clients...</div>
                          ) : (userClients[u.user_id] || []).length === 0 ? (
                            <div style={styles.tdSub}>No clients.</div>
                          ) : (
                            <div style={styles.clientList}>
                              {userClients[u.user_id].map(c => (
                                <div key={c.id} style={styles.clientRow}>
                                  <div>
                                    <div>{c.name}</div>
                                    {c.session_type && <div style={styles.tdSub}>{c.session_type}</div>}
                                  </div>
                                  <button
                                    style={styles.deleteBtn}
                                    onClick={() => handleDeleteClient(u.user_id, c)}
                                    disabled={deletingClientId === c.id}
                                  >
                                    {deletingClientId === c.id ? 'Deleting...' : 'Delete'}
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {users.length === 0 && (
                  <tr><td style={styles.td} colSpan={8}>No users yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'clients' && (
        <div style={styles.card}>
          <div style={styles.cardBody}>
            <input
              style={styles.searchInput}
              placeholder="Search by client name or coach..."
              value={clientSearch}
              onChange={e => setClientSearch(e.target.value)}
            />
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Client</th>
                  <th style={styles.th}>Session type</th>
                  <th style={styles.th}>Coach</th>
                  <th style={styles.th}></th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.map(c => (
                  <tr key={c.id}>
                    <td style={styles.td}>{c.name}</td>
                    <td style={styles.td}>{c.session_type || '—'}</td>
                    <td style={styles.td}>
                      <div>{c.coach_name}</div>
                      <div style={styles.tdSub}>{c.coach_email}</div>
                    </td>
                    <td style={styles.td}>
                      <button
                        style={styles.deleteBtn}
                        onClick={() => handleDeleteClient(c.user_id, c)}
                        disabled={deletingClientId === c.id}
                      >
                        {deletingClientId === c.id ? 'Deleting...' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredClients.length === 0 && (
                  <tr><td style={styles.td} colSpan={4}>{clients.length === 0 ? 'No clients yet.' : 'No clients match your search.'}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'feedback' && (
        <div>
          {feedback.length === 0 && <div style={styles.card}><div style={styles.cardBody}>No feedback submitted yet.</div></div>}
          {feedback.map(f => (
            <div key={f.id} style={styles.card}>
              <div style={styles.cardBody}>
                <div style={styles.feedbackHeader}>
                  <span style={styles.pill}>{f.category}</span>
                  <span style={styles.tdSub}>{f.user_email} · {formatDate(f.created_at)}</span>
                </div>
                <div style={styles.feedbackMessage}>{f.message}</div>
                {f.would_pay_for && <div style={styles.tdSub}>Would pay for: {f.would_pay_for}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'waitlist' && (
        <div style={styles.card}>
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Source</th>
                  <th style={styles.th}>Joined</th>
                </tr>
              </thead>
              <tbody>
                {waitlist.map(w => (
                  <tr key={w.id}>
                    <td style={styles.td}>{w.email}</td>
                    <td style={styles.td}>{w.source || '—'}</td>
                    <td style={styles.td}>{formatDate(w.created_at)}</td>
                  </tr>
                ))}
                {waitlist.length === 0 && (
                  <tr><td style={styles.td} colSpan={3}>No waitlist signups yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'usage' && (
        <div style={styles.card}>
          <div style={styles.cardBody}>
            <div style={styles.tdSub}>Claude API usage this calendar month, per coach. Sorted by cost — heaviest coach first.</div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Coach</th>
                  <th style={styles.th}>Requests</th>
                  <th style={styles.th}>Est. cost (month)</th>
                  <th style={styles.th}>% of quota</th>
                  <th style={styles.th}>Rate-limited</th>
                  <th style={styles.th}>Top feature</th>
                  <th style={styles.th}>Last used</th>
                </tr>
              </thead>
              <tbody>
                {usage.map(u => (
                  <tr key={u.user_id}>
                    <td style={styles.td}>{u.email || u.user_id}</td>
                    <td style={styles.td}>{u.request_count}</td>
                    <td style={styles.td}>${(u.estimated_cost_usd || 0).toFixed(2)}</td>
                    <td style={styles.td}>
                      {u.pct_of_quota != null ? (
                        <span style={{ ...styles.pill, ...(u.pct_of_quota >= 80 ? styles.pillWarn : styles.pillMuted) }}>
                          {u.pct_of_quota}%
                        </span>
                      ) : '—'}
                    </td>
                    <td style={styles.td}>{u.rate_limited_count > 0 ? u.rate_limited_count : '—'}</td>
                    <td style={styles.td}>{u.top_feature || '—'}</td>
                    <td style={styles.td}>{formatDate(u.last_used_at)}</td>
                  </tr>
                ))}
                {usage.length === 0 && (
                  <tr><td style={styles.td} colSpan={7}>No AI usage logged yet this month.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { padding: '36px 40px', fontFamily: "'DM Sans', sans-serif" },
  loadingState: { padding: '60px', textAlign: 'center', color: 'var(--text-soft)', fontSize: '14px' },
  header: { marginBottom: '24px' },
  pageTitle: { fontFamily: "'Lora', Georgia, serif", fontSize: '28px', fontWeight: '400', color: 'var(--text-dark)', marginBottom: '4px' },
  pageSubtitle: { fontSize: '13px', color: 'var(--text-soft)' },
  errorAlert: { background: 'var(--red-light)', color: 'var(--red)', padding: '12px 16px', borderRadius: '8px', fontSize: '13.5px' },
  toast: { background: 'var(--mint)', color: 'var(--sage)', padding: '12px 16px', borderRadius: '8px', fontSize: '13.5px', fontWeight: '500', marginBottom: '16px' },
  actionBtn: { padding: '7px 14px', borderRadius: '7px', background: 'none', border: '1px solid var(--border)', color: 'var(--sage)', fontFamily: "'DM Sans', sans-serif", fontSize: '12.5px', fontWeight: '500', cursor: 'pointer', whiteSpace: 'nowrap' },
  tabRow: { display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '1px solid var(--border)' },
  tab: { padding: '10px 18px', background: 'none', border: 'none', borderBottom: '2px solid transparent', color: 'var(--text-soft)', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', fontWeight: '500', cursor: 'pointer' },
  tabActive: { color: 'var(--sage)', borderBottomColor: 'var(--sage)' },
  card: { background: 'white', borderRadius: '14px', border: '1px solid var(--border)', marginBottom: '16px', overflow: 'hidden', boxShadow: 'var(--shadow-sm)' },
  cardBody: { padding: '18px 22px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: { textAlign: 'left', padding: '12px 16px', borderBottom: '1px solid var(--border)', color: 'var(--text-soft)', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' },
  td: { padding: '12px 16px', borderBottom: '1px solid var(--border-light)', color: 'var(--text-dark)', whiteSpace: 'nowrap' },
  tdSub: { fontSize: '11.5px', color: 'var(--text-soft)', marginTop: '2px' },
  pill: { display: 'inline-block', background: 'var(--mint)', color: 'var(--sage)', borderRadius: '20px', fontSize: '11px', fontWeight: '600', padding: '3px 10px' },
  pillGood: { background: 'var(--mint)', color: 'var(--sage)' },
  pillMuted: { background: 'var(--border-light)', color: 'var(--text-soft)' },
  pillWarn: { background: 'var(--red-light)', color: 'var(--red)' },
  feedbackHeader: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' },
  feedbackMessage: { fontSize: '14px', color: 'var(--text-dark)', lineHeight: 1.6 },
  linkBtn: { background: 'none', border: 'none', color: 'var(--sage)', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', fontWeight: '600', cursor: 'pointer', padding: 0 },
  expandedTd: { padding: '4px 16px 14px', borderBottom: '1px solid var(--border-light)', background: 'var(--cream)' },
  clientList: { display: 'flex', flexDirection: 'column', gap: '2px' },
  clientRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', borderRadius: '8px', fontSize: '13px', color: 'var(--text-dark)', background: 'white', border: '1px solid var(--border-light)' },
  deleteBtn: { padding: '6px 12px', borderRadius: '7px', background: 'none', border: '1px solid var(--red-light)', color: 'var(--red)', fontFamily: "'DM Sans', sans-serif", fontSize: '12px', fontWeight: '500', cursor: 'pointer', whiteSpace: 'nowrap' },
  searchInput: { width: '100%', maxWidth: '360px', padding: '9px 14px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--cream)', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', color: 'var(--text-dark)', outline: 'none', boxSizing: 'border-box' },
};
